import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { registerLocale } from "react-datepicker";
import pl from "date-fns/locale/pl";

import {
  makeStyles,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  TextField,
  Button,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";

import { usePageStyles } from "../common/pageStyles";
import { COLORS } from "../UserDashboard/dashboardConfig";

registerLocale("pl", pl);

const useStyles = makeStyles((theme) => ({
  panel: { padding: theme.spacing(2.5), marginBottom: theme.spacing(2.5) },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  listSelect: { minWidth: 320, flex: "0 1 420px" },
  searchField: { flex: "1 1 240px", minWidth: 200 },
  controlsGap: { flex: 1 },
  addForm: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${COLORS.border}`,
  },
  addInput: { flex: "1 1 320px", minWidth: 240 },
  tableWrap: { maxHeight: "calc(100vh - 340px)", minHeight: 160, overflow: "auto" },
  headCell: {
    background: "#f5f7fa",
    fontWeight: 700,
    color: COLORS.primaryDark,
    whiteSpace: "nowrap",
  },
  idCell: { width: 90, color: COLORS.textMuted },
  actionCell: { width: 90, textAlign: "right" },
  empty: { padding: theme.spacing(4), textAlign: "center", color: COLORS.textMuted },
  loading: { padding: theme.spacing(4), textAlign: "center" },
}));

const BASE_URL = API_BASE_URL;

const Lists = () => {
  const classes = useStyles();
  const page = usePageStyles();

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newRecordName, setNewRecordName] = useState("");
  const [isAddingRecord, setIsAddingRecord] = useState(false);

  // Dodaj stany na formularz osoby fizycznej
  const [isAddingPhysicalPerson, setIsAddingPhysicalPerson] = useState(false);
  const [newPhysicalPersonName, setNewPhysicalPersonName] = useState("");

  const [selectedValue, setSelectedValue] = useState(() => {
    return localStorage.getItem("selectedValue") || "";
  });

  // Update selected value and save it to localStorage
  const handleSelectChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
    localStorage.setItem("selectedValue", value);
    // Wyczyść obecne dane tabeli podczas zmiany
    setTableData([]);
  };

  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRow) {
      return;
    }

    try {
      // Make an API DELETE request to delete the selected row by its ID
      await axios.delete(
        `${BASE_URL}/delete/${selectedValue}/${selectedRow.id}`,
        {
          headers: getAuthHeaders(),
        },
      );

      // Pobierz zaktualizowane dane po udanym usunięciu
      await fetchData();

      toast.success(`Poprawnie usunięto ${selectedRow.name}!`, {
        autoClose: 3000,
      });

      //setIsDeleting(false); // Reset the delete state
      setSelectedRow(null); // Reset the selected row
    } catch (error) {
      console.error("Error deleting row:", error);
      toast.error("Wystąpił błąd podczas usuwania.", {
        autoClose: 3000,
      });
    }
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleNewRecordNameChange = (e) => {
    setNewRecordName(e.target.value);
  };

  const handleAddRecordClick = () => {
    setIsAddingRecord(true);
  };

  const handleCloseFormClick = () => {
    setNewRecordName("");
    setIsAddingRecord(false);
  };

  // Funkcja pomocnicza do sprawdzania duplikatów
  const checkDuplicateRecord = (nameToCheck) => {
    const normalizedName = nameToCheck.trim().toLowerCase();
    return tableData.some((row) => row.name.toLowerCase() === normalizedName);
  };

  const handleSaveRecordClick = async () => {
    if (!newRecordName.trim()) {
      toast.error("Nazwa nie może być pusta!", {
        autoClose: 3000,
      });
      return;
    }

    // Sprawdź czy rekord już istnieje
    if (checkDuplicateRecord(newRecordName)) {
      toast.error("Rekord o takiej nazwie już istnieje w liście!", {
        autoClose: 3000,
      });
      return;
    }

    try {
      if (
        selectedValue === "uzytkownicy" ||
        selectedValue === "lokalizacjaTransportow" ||
        selectedValue === "urzadZgloszenia"
      ) {
        await axios.post(
          `${BASE_URL}/add-record/${selectedValue}`,
          {
            name: newRecordName,
          },
          {
            headers: getAuthHeaders(),
          },
        );
      } else {
        // Convert newRecordName to uppercase
        const uppercaseName = newRecordName.trimStart().toUpperCase();
        // Make a POST request to your API with headers to save the new record
        await axios.post(
          `${BASE_URL}/add-record/${selectedValue}`,
          {
            name: uppercaseName,
          },
          {
            headers: getAuthHeaders(),
          },
        );
      }

      // Odśwież dane po dodaniu nowego rekordu
      await fetchData();

      // Reset states
      setNewRecordName("");
      setIsAddingRecord(false);

      toast.success("Poprawnie dodano nowy rekord!", {
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error adding new record:", error);
      toast.error("Wystąpił błąd podczas dodawania rekordu!", {
        autoClose: 3000,
      });
    }
  };

  const fetchData = useCallback(async () => {
    if (!selectedValue) return;

    setLoading(true);

    try {
      // Pobierz token z localStorage i ustaw nagłówki
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      // Wyślij żądanie GET z nagłówkami
      const response = await axios.get(`${BASE_URL}/list/${selectedValue}`, {
        headers: headers,
      });

      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedValue]);

  useEffect(() => {
    fetchData();
  }, [selectedValue, fetchData]);

  // Sort the table data alphabetically by name using localeCompare
  const sortedTableData = [...tableData].sort((a, b) => {
    const prefix = "OS. F";
    const aStartsWithPrefix = a.name.startsWith(prefix);
    const bStartsWithPrefix = b.name.startsWith(prefix);

    if (aStartsWithPrefix && !bStartsWithPrefix) {
      return 1; // Element a idzie na koniec
    } else if (!aStartsWithPrefix && bStartsWithPrefix) {
      return -1; // Element b idzie na koniec
    } else {
      // Jeśli oba lub żaden nie mają prefiksu, sortuj alfabetycznie
      return a.name.localeCompare(b.name, "pl", { sensitivity: "base" });
    }
  });

  // Filter sorted table data based on search term
  const filteredTableData = sortedTableData.filter((row) => {
    return row.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleAddPhysicalPersonClick = () => {
    setIsAddingPhysicalPerson(true);
  };

  // Handler zapisu osoby fizycznej – dostosuj endpoint i logikę według potrzeb
  const handleSavePhysicalPersonClick = async () => {
    if (!newPhysicalPersonName.trim()) {
      toast.error("Nazwa osoby fizycznej nie może być pusta!", {
        autoClose: 3000,
      });
      return;
    }

    // Sprawdź czy osoba fizyczna już istnieje
    if (checkDuplicateRecord(newPhysicalPersonName)) {
      toast.error("Osoba fizyczna o takiej nazwie już istnieje w liście!", {
        autoClose: 3000,
      });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      // Wywołanie endpointu z dynamiczną ścieżką w zależności od wybranej listy
      await axios.post(
        `${BASE_URL}/add-physical-person/${selectedValue}`,
        { name: newPhysicalPersonName },
        { headers: headers },
      );
      toast.success("Poprawnie dodano osobę fizyczną!", {
        autoClose: 3000,
      });
      // Odśwież dane i zaktualizuj stan tabeli
      await fetchData();
    } catch (error) {
      console.error("Błąd podczas dodawania osoby fizycznej:", error);
      toast.error("Wystąpił błąd podczas dodawania osoby fizycznej", {
        autoClose: 3000,
      });
    }
    setNewPhysicalPersonName("");
    setIsAddingPhysicalPerson(false);
  };

  // Handler zamykający formularz dodawania osoby fizycznej
  const handleClosePhysicalPersonClick = () => {
    setNewPhysicalPersonName("");
    setIsAddingPhysicalPerson(false);
  };

  const hasPhysicalPerson =
    selectedValue === "importerimport" ||
    selectedValue === "eksportereksport" ||
    selectedValue === "listMan";

  return (
    <div>
      <div className={page.pageHeader}>
        <div>
          <h1 className={page.title}>Listy</h1>
          <div className={page.subtitle}>
            Zarządzanie listami słownikowymi używanymi w formularzach
          </div>
        </div>
      </div>

      <div className={`${page.card} ${classes.panel}`}>
        <div className={classes.controls}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.listSelect}
          >
            <InputLabel id="lists-select-label">Wybierz listę</InputLabel>
            <Select
              labelId="lists-select-label"
              label="Wybierz listę"
              value={selectedValue}
              onChange={handleSelectChange}
            >
              <MenuItem value="importerimport">Lista Importerów</MenuItem>
              <MenuItem value="eksportereksport">Lista Eksporterów</MenuItem>
              <MenuItem value="listMan">Lista Eksporterów MAN BUS</MenuItem>
              <MenuItem value="lokalizacjaTransportow">
                Lista lokalizacji odpraw
              </MenuItem>
              <MenuItem value="urzadZgloszenia">
                Lista urzędów zgłoszenia
              </MenuItem>
              <MenuItem value="uzytkownicy">Lista użytkowników</MenuItem>
              <MenuItem value="urzad_tranzytowy">
                Lista przewidywanych urzędów tranzytowych
              </MenuItem>
              <MenuItem value="urzad_przeznaczenia">
                Lista urzędów celnych przeznaczenia
              </MenuItem>
            </Select>
          </FormControl>

          {selectedValue && (
            <>
              <TextField
                label="Wyszukaj po nazwie"
                variant="outlined"
                size="small"
                className={classes.searchField}
                value={searchTerm}
                onChange={handleSearchTermChange}
              />
              <div className={classes.controlsGap} />
              {hasPhysicalPerson && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAddPhysicalPersonClick}
                >
                  Dodaj osobę fizyczną
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddRecordClick}
              >
                Dodaj pozycję
              </Button>
            </>
          )}
        </div>

        {isAddingPhysicalPerson && (
          <div className={classes.addForm}>
            <TextField
              label="Nazwa osoby fizycznej"
              variant="outlined"
              size="small"
              className={classes.addInput}
              value={newPhysicalPersonName}
              onChange={(e) => setNewPhysicalPersonName(e.target.value)}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSavePhysicalPersonClick}
            >
              Zapisz
            </Button>
            <Button onClick={handleClosePhysicalPersonClick}>Anuluj</Button>
          </div>
        )}

        {isAddingRecord && (
          <div className={classes.addForm}>
            <TextField
              label="Nazwa"
              variant="outlined"
              size="small"
              className={classes.addInput}
              value={newRecordName}
              onChange={handleNewRecordNameChange}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveRecordClick}
            >
              Zapisz
            </Button>
            <Button onClick={handleCloseFormClick}>Anuluj</Button>
          </div>
        )}
      </div>

      {!selectedValue && (
        <div className={`${page.card} ${classes.empty}`}>
          Wybierz listę, aby zobaczyć jej pozycje.
        </div>
      )}

      {selectedValue && loading && (
        <div className={`${page.card} ${classes.loading}`}>
          <CircularProgress />
        </div>
      )}

      {selectedValue && !loading && (
        <div className={page.card}>
          <div className={classes.tableWrap}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell}>Nr.</TableCell>
                  <TableCell className={classes.headCell}>Nazwa</TableCell>
                  <TableCell
                    className={`${classes.headCell} ${classes.actionCell}`}
                  >
                    Usuń
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTableData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell className={classes.idCell}>{row.id}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className={classes.actionCell}>
                      {selectedRow && selectedRow.id === row.id ? (
                        <IconButton
                          size="small"
                          onClick={handleConfirmDelete}
                          title="Potwierdź usunięcie"
                        >
                          <DeleteIcon color="secondary" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(row)}
                          title="Usuń"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTableData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className={classes.empty}>
                      Brak pozycji.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lists;
